/**
 * Heatmap View
 * Visualisering av GPS-data som heatmap med olika lägen
 */

import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { useTranslation } from '@/store/languageStore';
import { PageBackButton } from '@/components/common/PageBackButton';
import { HeatmapPoint } from '@/services/heatmap/heatmapService';
import SearchableVehicleSelect from '@/components/vehicle/SearchableVehicleSelect';
import HierarchicalOrgSelect from '@/components/common/HierarchicalOrgSelect';
import { InformationCircleIcon, XMarkIcon, MapIcon } from '@heroicons/react/24/outline';
import { useHeatmap } from '@/hooks/useHeatmap';
import {
  HeatmapProps,
  HeatmapMode,
  TimePattern,
  HEATMAP_MODE_INFO,
  mapVehicleForSelect,
} from '@/types/heatmap';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import DateInput from '@/components/common/DateInput';
// Fix för Leaflet default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Heatmap Layer Component
const HeatmapLayer: React.FC<{ points: HeatmapPoint[]; mode: string }> = ({
  points,
  mode,
}) => {
  const map = useMap();
  const heatmapLayerRef = useRef<L.Layer | null>(null);
  const hasFittedBoundsRef = useRef(false);

  useEffect(() => {
    if (!points || points.length === 0) {
      if (heatmapLayerRef.current) {
        map.removeLayer(heatmapLayerRef.current);
        heatmapLayerRef.current = null;
      }
      hasFittedBoundsRef.current = false;
      return;
    }

    if (heatmapLayerRef.current) {
      map.removeLayer(heatmapLayerRef.current);
    }

    const heatmapGroup = L.layerGroup();
    const validPoints: Array<{ lat: number; lng: number }> = [];

    points.forEach((point) => {
      if (
        !point.lat ||
        !point.lng ||
        (point.lat === 0 && point.lng === 0) ||
        point.lat < -90 ||
        point.lat > 90 ||
        point.lng < -180 ||
        point.lng > 180
      ) {
        return;
      }

      validPoints.push({ lat: point.lat, lng: point.lng });

      const intensity = point.intensity || 1;
      const count = point.count || intensity;

      if (mode === 'parking' || mode === 'charging' || mode === 'incidents') {
        let markerColor: string;
        if (count >= 11) {
          markerColor = '#dc2626';
        } else if (count >= 6) {
          markerColor = '#f97316';
        } else if (count >= 3) {
          markerColor = '#eab308';
        } else {
          markerColor = '#22c55e';
        }

        let title: string;
        if (mode === 'parking') {
          title = 'Parkeringar';
        } else if (mode === 'charging') {
          title = 'Laddningsstationer';
        } else {
          title = 'Olyckor/Incidenter';
        }

        const markerIcon = L.divIcon({
          className: `${mode}-marker`,
          html: `<div style="
            width: 20px;
            height: 20px;
            background-color: ${markerColor};
            border: 2px solid white;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          "></div>`,
          iconSize: [20, 20],
          iconAnchor: [10, 10],
        });

        const marker = L.marker([point.lat, point.lng], { icon: markerIcon });
        const circle = L.circle([point.lat, point.lng], {
          radius: 100,
          fillColor: markerColor,
          color: markerColor,
          weight: 2,
          opacity: 0.6,
          fillOpacity: 0.2,
        });

        const popupContent = `
          <div style="text-align: center;">
            <strong>${title}</strong><br/>
            Antal: ${count}<br/>
            Radie: 100m
          </div>
        `;
        marker.bindPopup(popupContent);
        circle.bindPopup(popupContent);

        heatmapGroup.addLayer(marker);
        heatmapGroup.addLayer(circle);
      } else {
        const radius = Math.max(50, Math.min(250, 50 + (intensity - 1) * 22.2));
        const opacity = Math.min(0.85, 0.3 + (intensity - 1) * 0.06);

        let color: string;
        if (intensity >= 9) {
          color = '#cc0000';
        } else if (intensity >= 7) {
          color = '#ff3300';
        } else if (intensity >= 5) {
          color = '#ff6600';
        } else if (intensity >= 3) {
          color = '#ff9900';
        } else {
          color = '#ffcc00';
        }

        const circle = L.circle([point.lat, point.lng], {
          radius,
          fillColor: color,
          color,
          weight: 1,
          opacity: 0.8,
          fillOpacity: opacity,
        });

        heatmapGroup.addLayer(circle);
      }
    });

    heatmapGroup.addTo(map);
    heatmapLayerRef.current = heatmapGroup;

    if (validPoints.length > 0) {
      try {
        const bounds = L.latLngBounds(
          validPoints.map((p) => [p.lat, p.lng] as [number, number])
        );

        const isValidBounds =
          bounds.isValid() &&
          (bounds.getNorth() - bounds.getSouth() > 0.001 ||
            bounds.getEast() - bounds.getWest() > 0.001);

        if (isValidBounds) {
          const padding: [number, number] =
            validPoints.length > 100 ? [100, 100] : [50, 50];
          map.fitBounds(bounds, { padding, maxZoom: 15, animate: true });
          hasFittedBoundsRef.current = true;
        } else {
          const center = validPoints[0];
          map.setView([center.lat, center.lng], 15, { animate: true });
          hasFittedBoundsRef.current = true;
        }
      } catch {
        // Error fitting bounds - ignored
      }
    }

    return () => {
      if (heatmapLayerRef.current) {
        map.removeLayer(heatmapLayerRef.current);
      }
    };
  }, [points, map, mode]);

  return null;
};

const Heatmap: React.FC<HeatmapProps> = ({ sessionId, isImport = false }) => {
  const { t } = useTranslation();
  const {
    vehicles,
    points,
    tripCount,
    selectedOrganizationId,
    selectedVehicleId,
    mode,
    timePattern,
    fromDate,
    toDate,
    loading,
    loadingVehicles,
    showInfoModal,
    setFromDate,
    setToDate,
    loadHeatmapData,
    handleVehicleChange,
    handleSelectAll,
    handleOrganizationChange,
    handleModeChange,
    handleTimePatternChange,
    openInfoModal,
    closeInfoModal,
  } = useHeatmap({ sessionId, isImport });

  return (
    <div className="h-full flex flex-col bg-bgContainer">
      {/* Header med filter */}
      <div className="relative z-10 p-4 bg-bgElevated border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <PageBackButton />
            <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
              <MapIcon className="w-6 h-6 text-primary" />
              {isImport
                ? t('heatmapImport.title') || 'Heatmap Import'
                : t('heatmap.title') || 'Heatmap'}
            </h1>
          </div>
          <button
            onClick={openInfoModal}
            className="p-2 text-textSecondary hover:text-textDefault transition-colors"
            title={
              t('heatmap.showInfo') || 'Visa information om analyslägen'
            }
          >
            <InformationCircleIcon className="w-6 h-6" />
          </button>
        </div>

        <div
          className={`grid grid-cols-1 md:grid-cols-2 ${
            mode === 'timepattern' ? 'lg:grid-cols-6' : 'lg:grid-cols-5'
          } gap-4`}
        >
          {/* Organisationsval */}
          {!isImport && (
            <div>
              <label className="block text-sm font-medium text-textSecondary mb-2">
                {t('heatmap.organization') || 'Organisation'}
              </label>
              <HierarchicalOrgSelect
                value={selectedOrganizationId}
                onChange={handleOrganizationChange}
                placeholder={
                  t('heatmap.selectOrganization') || 'Välj organisation...'
                }
              />
            </div>
          )}

          {/* Fordonsval */}
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-2">
              {t('heatmap.vehicles') || 'Fordon'}
            </label>
            <div className="flex gap-2">
              <SearchableVehicleSelect
                vehicles={vehicles.map((v) => mapVehicleForSelect(v, isImport))}
                selectedValue={selectedVehicleId}
                onChange={handleVehicleChange}
                placeholder={
                  isImport
                    ? t('heatmapImport.selectVehicle') || 'Välj fordon...'
                    : selectedOrganizationId
                    ? t('heatmap.selectVehicleFromOrg') || 'Välj fordon...'
                    : t('heatmap.selectVehicle') || 'Välj fordon...'
                }
                className="flex-1"
                loading={loadingVehicles}
                disabled={loadingVehicles}
              />
              <button
                onClick={handleSelectAll}
                className="px-3 py-2 bg-bgPrimary text-white rounded hover:bg-bgPrimary/90 text-sm"
                title={
                  selectedOrganizationId
                    ? t('heatmap.allVehiclesInOrg') ||
                      'Alla fordon i organisationen'
                    : t('heatmap.allVehicles') || 'Alla fordon'
                }
              >
                {t('heatmap.allVehicles') || 'Alla'}
              </button>
            </div>
            {selectedOrganizationId && vehicles.length > 0 && (
              <p className="text-xs text-textSecondary mt-1">
                {vehicles.length}{' '}
                {t('heatmap.vehiclesInOrg') || 'fordon i organisationen'}
              </p>
            )}
          </div>

          {/* Tidsperiod */}
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-2">
              {t('heatmap.fromDate') || 'Från datum'}
            </label>
            <DateInput value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded bg-bgContainer text-textDefault"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-textSecondary mb-2">
              {t('heatmap.toDate') || 'Till datum'}
            </label>
            <DateInput value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded bg-bgContainer text-textDefault"
            />
          </div>

          {/* Läge */}
          <div>
            <label className="block text-sm font-medium text-textSecondary mb-2">
              {t('heatmap.mode') || 'Läge'}
            </label>
            <select
              value={mode}
              onChange={(e) => handleModeChange(e.target.value as HeatmapMode)}
              className="w-full px-3 py-2 border border-border rounded bg-bgContainer text-textDefault"
            >
              <option value="all">
                {t('heatmap.modeAll') || 'Alla punkter'}
              </option>
              <option value="overspeed">
                {t('heatmap.modeOverspeed') || 'Hastighetsöverträdelser'}
              </option>
              <option value="idling">
                {t('heatmap.modeIdling') || 'Idling/Tomgång'}
              </option>
              <option value="parking">
                {t('heatmap.modeParking') || 'Parkeringar'}
              </option>
              <option value="eco">
                {t('heatmap.modeEco') || 'ECO-händelser'}
              </option>
              <option value="timepattern">
                {t('heatmap.modeTimePattern') || 'Tidsbaserade mönster'}
              </option>
              <option value="geofence">
                {t('heatmap.modeGeofence') || 'Geofence-överträdelser'}
              </option>
              <option value="incidents">
                {t('heatmap.modeIncidents') || 'Olyckor/Incidenter'}
              </option>
              <option value="charging">
                {t('heatmap.modeCharging') || 'Laddningsstationer'}
              </option>
              <option value="riskscore">
                {t('heatmap.modeRiskScore') || 'Kombinerad risk-score'}
              </option>
            </select>
          </div>

          {/* Tidsmönster */}
          {mode === 'timepattern' && (
            <div>
              <label className="block text-sm font-medium text-textSecondary mb-2">
                {t('heatmap.timePattern') || 'Tidsmönster'}
              </label>
              <select
                value={timePattern}
                onChange={(e) =>
                  handleTimePatternChange(e.target.value as TimePattern)
                }
                className="w-full px-3 py-2 border border-border rounded bg-bgContainer text-textDefault"
              >
                <option value="all">
                  {t('heatmap.timePatternAll') || 'Alla tider'}
                </option>
                <option value="morning">
                  {t('heatmap.timePatternMorning') || 'Morgon (06:00-09:00)'}
                </option>
                <option value="day">
                  {t('heatmap.timePatternDay') || 'Dag (09:00-17:00)'}
                </option>
                <option value="evening">
                  {t('heatmap.timePatternEvening') || 'Kväll (17:00-22:00)'}
                </option>
                <option value="night">
                  {t('heatmap.timePatternNight') || 'Natt (22:00-06:00)'}
                </option>
              </select>
            </div>
          )}
        </div>

        {/* Ladda-knapp */}
        <div className="mt-4 flex items-center gap-4">
          <button
            onClick={loadHeatmapData}
            disabled={loading}
            className="px-4 py-2 bg-bgPrimary text-white rounded hover:bg-bgPrimary/90 disabled:opacity-50"
          >
            {loading
              ? t('heatmap.loading') || 'Laddar...'
              : t('heatmap.loadData') || 'Ladda data'}
          </button>
          {tripCount > 0 && (
            <span className="text-sm text-textSecondary">
              {t('heatmap.tripCount')
                ?.replace('{count}', String(tripCount))
                ?.replace('{points}', String(points.length)) ||
                `${tripCount} trippar, ${points.length} punkter`}
            </span>
          )}
        </div>
      </div>

      {/* Karta */}
      <div className="flex-1 relative z-0">
        <MapContainer
          center={[59.3293, 18.0686]}
          zoom={6}
          className="h-full w-full"
          scrollWheelZoom={true}
        >
          <TileLayer
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />
          <HeatmapLayer points={points} mode={mode} />
        </MapContainer>
      </div>

      {/* Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4">
          <div className="bg-bgContainer rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-bgContainer border-b border-border p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-textDefault">
                {t('heatmap.infoTitle') || 'Information om analyslägen'}
              </h2>
              <button
                onClick={closeInfoModal}
                className="p-1 hover:bg-bgElevated rounded transition-colors"
              >
                <XMarkIcon className="w-6 h-6 text-textSecondary" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {(Object.keys(HEATMAP_MODE_INFO) as HeatmapMode[]).map(
                (modeKey) => {
                  const info = HEATMAP_MODE_INFO[modeKey];
                  return (
                    <div key={modeKey}>
                      <h3 className="font-semibold text-textDefault mb-2">
                        {t(info.titleKey) || info.fallbackTitle}
                      </h3>
                      <p className="text-sm text-textSecondary">
                        {t(info.descriptionKey) || info.fallbackDescription}
                      </p>
                    </div>
                  );
                }
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Heatmap;
